import { useMemo, useState } from "react";
import { Link } from "react-router";
import { formatIQD } from "@fanni/shared/money";
import { PAYMENT_METHOD_AR, fmtDateTime } from "../lib/format.js";
import { useInvoices, usePayments } from "../lib/queries.js";
import { PaymentModal } from "./Invoices.js";
import {
  Badge,
  Card,
  Empty,
  ErrorNote,
  Loading,
  PageHeader,
  Select,
  Stat,
  Table,
  Td,
  Th,
  Tr,
} from "../ui/kit.js";

export function Payments() {
  const { data, isLoading, error } = usePayments();
  const invoices = useInvoices();
  const [pickingFor, setPickingFor] = useState<string>("");
  const [method, setMethod] = useState<string>("");

  const rows = useMemo(() => {
    const all = data?.payments ?? [];
    return method ? all.filter((p) => p.method === method) : all;
  }, [data, method]);

  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const collectedToday = rows
    .filter((p) => new Date(p.receivedAt) >= todayStart)
    .reduce((a, p) => a + p.amountFils, 0);
  const total = rows.reduce((a, p) => a + p.amountFils, 0);

  /** Only unpaid invoices can take a payment, so only they are offered. */
  const payable = useMemo(
    () =>
      (invoices.data?.invoices ?? []).filter(
        (i) => i.outstanding_fils > 0 && i.status !== "void" && i.status !== "draft",
      ),
    [invoices.data],
  );

  const selected = payable.find((i) => i.id === pickingFor);

  return (
    <>
      <PageHeader
        title="المقبوضات"
        subtitle="ما دخل الصندوق فعلاً، ومن قبضه."
        actions={
          <div className="flex items-center gap-2">
            <Select value={method} onChange={(e) => setMethod(e.target.value)} className="w-32">
              <option value="">كل الطرق</option>
              {Object.entries(PAYMENT_METHOD_AR).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
            <Select value={pickingFor} onChange={(e) => setPickingFor(e.target.value)} className="w-64">
              <option value="">— اختر فاتورة لتسجيل دفعة —</option>
              {payable.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.number} · {i.customerName} · {formatIQD(i.outstanding_fils)}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      {error ? <ErrorNote error={error} className="mb-4" /> : null}

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Stat label="المقبوض اليوم" value={<span className="num">{formatIQD(collectedToday)}</span>} tone="good" />
        <Stat
          label="مجموع المعروض"
          value={<span className="num">{formatIQD(total)}</span>}
          hint={`${rows.length} دفعة`}
        />
        <Stat label="فواتير تنتظر التحصيل" value={<span className="num">{payable.length}</span>} tone="warn" />
      </div>

      <Card bodyClassName="">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>لا توجد دفعات مسجّلة.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>المبلغ</Th>
                <Th>الطريقة</Th>
                <Th>الفاتورة</Th>
                <Th>الزبون</Th>
                <Th>المرجع</Th>
                <Th>قبضها</Th>
                <Th>التاريخ</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <Tr key={p.id}>
                  <Td>
                    <span className="num font-bold text-good">{formatIQD(p.amountFils)}</span>
                  </Td>
                  <Td>
                    <Badge tone="neutral">{PAYMENT_METHOD_AR[p.method]}</Badge>
                  </Td>
                  <Td>
                    <Link to={`/invoices/${p.invoiceId}`} className="num text-accent hover:underline">
                      {p.invoiceNumber ?? "—"}
                    </Link>
                  </Td>
                  <Td className="font-medium">{p.customerName ?? "—"}</Td>
                  <Td>
                    <span className="num text-ink-soft">{p.reference ?? "—"}</span>
                  </Td>
                  <Td className="text-ink-soft">{p.receivedBy ?? "—"}</Td>
                  <Td>
                    <span className="num text-ink-soft">{fmtDateTime(p.receivedAt)}</span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/*
        Wallet collection (QiCard/ZainCash/FastPay) is out of MVP by an explicit decision in
        ARCHITECTURE.md — the columns exist so it is a migration, not a redesign. Cash,
        transfer and cheque are the only methods the API accepts today.
      */}
      <p className="mt-3 text-xs text-ink-faint">
        الطرق المتاحة: نقد، حوالة، صك. التحصيل عبر المحافظ الإلكترونية خارج نطاق هذه النسخة عمداً.
      </p>

      {selected ? <PaymentModal invoice={selected} onClose={() => setPickingFor("")} /> : null}
    </>
  );
}
